import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CalendarX, ArrowLeft, Trash2, Plus } from "lucide-react";
import { DatePicker } from "../../components/DatePicker";

function todayPlus(d) {
  const dt = new Date();
  dt.setDate(dt.getDate() + d);
  return dt.toISOString().split("T")[0];
}

export default function CarAvailability() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [data, setData] = useState({ blocks: [], bookings: [] });
  const [form, setForm] = useState({ start_date: todayPlus(1), end_date: todayPlus(3), reason: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [carRes, availRes] = await Promise.all([
        api.get(`/cars/${id}`),
        api.get(`/cars/${id}/availability`),
      ]);
      setCar(carRes.data);
      setData(availRes.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const addBlock = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/cars/${id}/availability`, form);
      toast.success("Dates blocked");
      setForm({ start_date: todayPlus(1), end_date: todayPlus(3), reason: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const removeBlock = async (blockId) => {
    if (!window.confirm("Remove this block?")) return;
    try {
      await api.delete(`/cars/${id}/availability/${blockId}`);
      toast.success("Block removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="car-availability-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Button asChild variant="ghost" size="sm" className="mb-2 -ml-3"><Link to="/owner/cars"><ArrowLeft size={14} className="mr-1.5" /> Back to cars</Link></Button>
          <h3 className="font-display text-2xl font-semibold tracking-tight">{car?.title || "Availability"}</h3>
          <p className="text-sm text-muted-foreground">Block dates when your car is unavailable (maintenance, personal use, etc.). Confirmed bookings appear below too.</p>
        </div>
      </div>

      <Card className="border-border p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3">Add new block</div>
        <form onSubmit={addBlock} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="add-block-form">
          <div>
            <Label className="text-xs">From</Label>
            <DatePicker testId="block-start-input" value={form.start_date} min={todayPlus(0)} onChange={(v) => setForm({ ...form, start_date: v })} />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <DatePicker testId="block-end-input" value={form.end_date} min={form.start_date || todayPlus(0)} onChange={(v) => setForm({ ...form, end_date: v })} />
          </div>
          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <Input className="mt-1.5" placeholder="Maintenance" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="block-reason-input" />
          </div>
          <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="add-block-btn">
            <Plus size={14} className="mr-1.5" /> {saving ? "Blocking…" : "Block dates"}
          </Button>
        </form>
      </Card>

      <Card className="border-border p-6">
        <h4 className="font-display text-lg font-semibold tracking-tight mb-3 flex items-center gap-2"><CalendarX size={16} /> Your blocked ranges</h4>
        {data.blocks.length === 0 ? (
          <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center rounded-md">No blocks yet. Your car is available on all dates not booked.</div>
        ) : (
          <div className="space-y-2" data-testid="blocks-list">
            {data.blocks.map((b) => (
              <div key={b.id} className="flex items-center justify-between border-b border-border last:border-b-0 pb-2 last:pb-0" data-testid={`block-row-${b.id}`}>
                <div>
                  <div className="font-display font-medium">{b.start_date} → {b.end_date}</div>
                  {b.reason && <div className="text-xs text-muted-foreground">{b.reason}</div>}
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeBlock(b.id)} data-testid={`remove-block-${b.id}`}><Trash2 size={14} /></Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="border-border p-6">
        <h4 className="font-display text-lg font-semibold tracking-tight mb-3">Existing bookings (auto-blocked)</h4>
        {data.bookings.length === 0 ? (
          <div className="text-sm text-muted-foreground">No upcoming bookings.</div>
        ) : (
          <div className="space-y-2">
            {data.bookings.map((b, i) => (
              <div key={i} className="flex items-center justify-between border-b border-border last:border-b-0 pb-2 last:pb-0">
                <div className="font-display font-medium">{b.start_date} → {b.end_date}</div>
                <Badge variant="outline" className="capitalize">{b.status?.replace("_", " ")}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
